import { useRef, useCallback } from 'react';
import useResize from 'js/components/elements/GenAiChat/hooks/useResize';

export const useDragAndResize = () => {
  const modalRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const { handleResizeMouseDown } = useResize(modalRef as React.RefObject<HTMLElement>);

  const handleMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!modalRef.current) return;

    event.preventDefault();

    const modal = modalRef.current;
    const modalRect = modal.getBoundingClientRect();
    const parentElement = modal.offsetParent as HTMLElement;

    if (!parentElement) return;

    const parentRect = parentElement.getBoundingClientRect();

    dragOffset.current = {
      x: event.clientX - modalRect.left,
      y: event.clientY - modalRect.top,
    };

    isDragging.current = true;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current || !modalRef.current || !parentElement) return;

      const newX = e.clientX - parentRect.left - dragOffset.current.x;
      const newY = e.clientY - parentRect.top - dragOffset.current.y;

      const maxX = parentElement.offsetWidth - modalRef.current.offsetWidth;
      const maxY = parentElement.offsetHeight - modalRef.current.offsetHeight;

      const constrainedX = Math.max(0, Math.min(newX, maxX));
      const constrainedY = Math.max(0, Math.min(newY, maxY));

      modalRef.current.style.left = `${constrainedX}px`;
      modalRef.current.style.top = `${constrainedY}px`;
      modalRef.current.style.right = 'auto';
      modalRef.current.style.bottom = 'auto';
      modalRef.current.style.transform = 'none';
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  return {
    modalRef,
    handleMouseDown,
    handleResizeMouseDown,
  };
};
